import React, { useState, useEffect } from 'react';
import { ArrowLeft, Target, Zap, Network, Shield, Users, Code, GitBranch, TrendingUp, Check } from 'lucide-react';
import { Link } from 'react-router-dom';


export default function VisionPage() {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-gray-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors font-mono text-sm uppercase tracking-wider">
        <ArrowLeft size={18} />
        Back
        </Link>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            <span className="text-blue-400 font-mono text-xs uppercase tracking-wider">The Vision</span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-light mb-8 text-white leading-[0.9] tracking-tighter">
            The Vision:<br />
            <span className="text-blue-400">Execution Infrastructure</span><br />
            <span className="text-gray-600">That Actually Works</span>
          </h1>
          
          <p className="text-2xl md:text-3xl text-gray-400 font-light leading-relaxed mb-12">
            What we're building, why it matters, and how we get there together.
          </p>
          
          <div className="border-l-2 border-blue-500/30 pl-8 py-6">
            <p className="text-xl text-gray-300 leading-relaxed">
              This isn't about creating another product in an oversaturated market. It's about building the infrastructure layer that DeFi has needed since day one—and doing it with the people who will actually use it.
            </p>
          </div>
        </div>
      </section>

      {/* The Current State */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            The Current State<br />
            <span className="text-gray-600">Is Unacceptable</span>
          </h2>
          
          <div className="space-y-12 mb-16">
            <div className="bg-white/5 border border-gray-800 p-8 md:p-12">
              <h3 className="text-2xl font-light text-white mb-6">DeFi moved $1.5 trillion last year.</h3>
              <p className="text-xl text-gray-400 leading-relaxed mb-6">
                Yet the infrastructure supporting large trades is fundamentally broken. Every aggregator optimizes for the same thing: retail volume metrics that look good to VCs.
              </p>
              <p className="text-xl text-gray-400 leading-relaxed">
                Nobody is building for the $100k+ trades. Nobody is solving for cross-chain complexity at scale. Nobody is addressing the real execution costs that compound into millions of dollars lost annually.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-red-500/20 bg-red-500/5 p-8">
                <div className="text-4xl font-mono text-red-400 mb-3">2-4%</div>
                <p className="text-sm text-gray-400">Average execution loss per large trade</p>
              </div>
              <div className="border border-red-500/20 bg-red-500/5 p-8">
                <div className="text-4xl font-mono text-red-400 mb-3">$408k</div>
                <p className="text-sm text-gray-400">Lost annually on $2M monthly volume</p>
              </div>
              <div className="border border-red-500/20 bg-red-500/5 p-8">
                <div className="text-4xl font-mono text-red-400 mb-3">0</div>
                <p className="text-sm text-gray-400">Solutions addressing this problem</p>
              </div>
            </div>
          </div>
          
          <div className="border-l-2 border-blue-500/30 pl-8 py-6">
            <p className="text-xl text-white font-light mb-4">
              This isn't a technology problem. It's an incentive problem.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              VCs fund products that maximize retail user counts. Founders build for token launches. Nobody's building infrastructure that takes years and serves a smaller, more sophisticated user base—even though that's where the real value is.
            </p>
          </div>
        </div>
      </section>

      {/* What We're Building */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            What We're Building
          </h2>
          
          <div className="space-y-16">
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Network className="text-blue-400" size={40} strokeWidth={1.5} />
                <h3 className="text-3xl font-light text-white">Parallel Execution Architecture</h3>
              </div>
              <div className="space-y-6 ml-14">
                <p className="text-xl text-gray-400 leading-relaxed">
                  Instead of routing through a single path that can fail, we decompose large trades into multiple parallel execution paths. Each path operates independently, monitoring its own conditions in real-time.
                </p>
                <div className="bg-white/5 border border-gray-800 p-6">
                  <p className="text-gray-300 font-mono text-sm mb-4">Example: $400k USDC → ETH</p>
                  <div className="space-y-2 text-sm text-gray-400 font-mono">
                    <p>→ Path 1: $140k via Bridge A + Uniswap V3</p>
                    <p>→ Path 2: $120k via Bridge B + Curve</p>
                    <p>→ Path 3: $80k native swap + bridge ETH</p>
                    <p>→ Path 4: $60k via Bridge C + Aerodrome</p>
                  </div>
                </div>
                <p className="text-lg text-gray-400 leading-relaxed">
                  If one path encounters an issue—pool imbalance, gas spike, bridge congestion—the others continue executing. Path independence equals resilience.
                </p>
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Zap className="text-blue-400" size={40} strokeWidth={1.5} />
                <h3 className="text-3xl font-light text-white">Real-Time Adaptive Routing</h3>
              </div>
              <div className="space-y-6 ml-14">
                <p className="text-xl text-gray-400 leading-relaxed">
                  Each execution path continuously monitors market conditions, gas prices, pool liquidity, and bridge health. When conditions change, paths recalculate and reroute in under 1 second.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border border-gray-800 bg-black p-6">
                    <p className="text-white font-mono text-sm mb-4 uppercase tracking-wider">Static Routing (Current)</p>
                    <div className="space-y-2 text-xs text-gray-400">
                      <p>✗ Check price once</p>
                      <p>✗ Execute on stale data</p>
                      <p>✗ Hope nothing changes</p>
                      <p>✗ Accept losses when it does</p>
                    </div>
                  </div>
                  <div className="border border-green-500/20 bg-green-500/5 p-6">
                    <p className="text-green-400 font-mono text-sm mb-4 uppercase tracking-wider">Adaptive Routing (Us)</p>
                    <div className="space-y-2 text-xs text-gray-400">
                      <p>✓ Continuous monitoring</p>
                      <p>✓ Sub-second recalculation</p>
                      <p>✓ Automatic rerouting</p>
                      <p>✓ Prevent losses before they happen</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Shield className="text-blue-400" size={40} strokeWidth={1.5} />
                <h3 className="text-3xl font-light text-white">MEV Protection & Bridge Intelligence</h3>
              </div>
              <div className="space-y-6 ml-14">
                <p className="text-xl text-gray-400 leading-relaxed">
                  Large trades leak information. Our architecture minimizes this through path splitting, timing optimization, and intelligent bridge selection based on real-time health scoring.
                </p>
                <div className="bg-white/5 border border-gray-800 p-6">
                  <p className="text-white font-mono text-sm mb-4 uppercase tracking-wider">Bridge Selection Algorithm</p>
                  <div className="space-y-2 text-sm text-gray-400">
                    <p>• Historical reliability scoring</p>
                    <p>• Current congestion levels</p>
                    <p>• Optimal path for trade size</p>
                    <p>• MEV risk assessment</p>
                    <p>• Gas efficiency analysis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            Why Now
          </h2>
          
          <div className="space-y-8 mb-12">
            <p className="text-2xl text-gray-300 leading-relaxed font-light">
              The infrastructure is finally ready. Cross-chain messaging has matured. Bridge reliability is understood. We can build what wasn't possible 2 years ago.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-800 bg-black p-8">
                <h4 className="text-xl font-mono text-white uppercase tracking-wider mb-6">Technical Readiness</h4>
                <div className="space-y-3 text-gray-400">
                  <p>✓ Mature cross-chain protocols</p>
                  <p>✓ Reliable bridge infrastructure</p>
                  <p>✓ Sub-second execution monitoring</p>
                  <p>✓ Proven parallel processing patterns</p>
                </div>
              </div>
              <div className="border border-gray-800 bg-black p-8">
                <h4 className="text-xl font-mono text-white uppercase tracking-wider mb-6">Market Readiness</h4>
                <div className="space-y-3 text-gray-400">
                  <p>✓ Users understand the problem</p>
                  <p>✓ Existing solutions clearly inadequate</p>
                  <p>✓ Volume justifies infrastructure investment</p>
                  <p>✓ Community ready to build together</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-l-2 border-blue-500/30 pl-8 py-6">
            <p className="text-xl text-white font-light mb-4">
              But the real reason is simpler:
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Nobody else is going to build this. VCs won't fund it. Founders won't prioritize it. It has to come from the community that needs it.
            </p>
          </div>
        </div>
      </section>

      {/* The Community Build Model */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            The Community Build Model
          </h2>
          
          <div className="space-y-12">
            <p className="text-2xl text-gray-300 leading-relaxed font-light">
              Traditional protocols optimize for token launch day. We're optimizing for year 5.
            </p>
            
            <div className="space-y-6">
              <div className="border border-gray-800 bg-black p-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 font-mono text-blue-400">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-light text-white mb-3">250 Builders Shape Direction</h4>
                    <p className="text-gray-400 leading-relaxed">
                      Not users. Not token buyers. People who trade this volume and understand what needs to exist. Core features are defined by the people who will depend on them.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border border-gray-800 bg-black p-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 font-mono text-blue-400">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-light text-white mb-3">Transparent Development</h4>
                    <p className="text-gray-400 leading-relaxed">
                      Monthly updates on progress. Real metrics. Honest timelines. No hype cycles. No promises we can't keep. Just builders building.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border border-gray-800 bg-black p-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 font-mono text-blue-400">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-light text-white mb-3">Test With Real Money</h4>
                    <p className="text-gray-400 leading-relaxed">
                      Beta group tests with actual trades. Not simulations. Not testnet. Real capital, real feedback, real iteration until it actually works.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border border-gray-800 bg-black p-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 font-mono text-blue-400">
                    4
                  </div>
                  <div>
                    <h4 className="text-xl font-light text-white mb-3">Launch When It Works</h4>
                    <p className="text-gray-400 leading-relaxed">
                      Not on a predetermined timeline. Not when the token needs liquidity. When the infrastructure is proven and the community confirms it solves the problem.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border border-green-500/20 bg-green-500/5 p-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-green-500/10 border border-green-500/30 flex items-center justify-center flex-shrink-0 font-mono text-green-400">
                    5
                  </div>
                  <div>
                    <h4 className="text-xl font-light text-white mb-3">Everyone Stays Aligned</h4>
                    <p className="text-gray-400 leading-relaxed mb-4">
                      Token distribution weighted by volume and involvement. Vesting tied to usage milestones. No team allocations that dump. No VC unlocks that crater the price.
                    </p>
                    <p className="text-green-400 font-mono text-sm">
                      → If the infrastructure works, everyone wins. If it doesn't, we all built it wrong together.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Success Looks Like */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            What Success Looks Like
          </h2>
          
          <div className="space-y-12">
            <p className="text-2xl text-gray-300 leading-relaxed font-light">
              We don't want to be "the best aggregator." We want to be the invisible infrastructure layer that every serious trader depends on.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-800 bg-black p-8">
                <h4 className="text-xl font-mono text-white uppercase tracking-wider mb-6">Year 1</h4>
                <div className="space-y-3 text-gray-400">
                  <p>• 250 core users routing $100M+ monthly</p>
                  <p>• Average execution improvement: 1.5-3%</p>
                  <p>• 20+ chains supported</p>
                  <p>• Proven infrastructure reliability</p>
                </div>
              </div>
              <div className="border border-gray-800 bg-black p-8">
                <h4 className="text-xl font-mono text-white uppercase tracking-wider mb-6">Year 3</h4>
                <div className="space-y-3 text-gray-400">
                  <p>• Other aggregators integrate our routing</p>
                  <p>• Wallets use us as backend</p>
                  <p>• $1B+ routed monthly</p>
                  <p>• The standard for large-scale execution</p>
                </div>
              </div>
            </div>
            
            <div className="border-l-2 border-blue-500/30 pl-8 py-6">
              <p className="text-xl text-white font-light mb-4">
                Success means being boring infrastructure
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                No drama. No governance wars. No token crashes. Just execution infrastructure that works so well nobody thinks about it anymore.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risks & Honesty */}
      {/* <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light mb-12 text-white tracking-tight">
            The Honest Part
          </h2>
          
          <div className="space-y-8">
            <p className="text-2xl text-gray-300 leading-relaxed font-light">
              This is hard. Most attempts at infrastructure like this fail. Here's what could go wrong:
            </p>
            
            <div className="space-y-6">
              <div className="border border-yellow-500/20 bg-yellow-500/5 p-8">
                <h4 className="text-lg font-mono text-yellow-400 uppercase tracking-wider mb-4">Technical Risk</h4>
                <p className="text-gray-400 leading-relaxed">
                  Parallel execution at this scale hasn't been proven in production. We might discover edge cases that make the approach unviable. We might need to pivot the architecture 6 months in.
                </p>
              </div>
              
              <div className="border border-yellow-500/20 bg-yellow-500/5 p-8">
                <h4 className="text-lg font-mono text-yellow-400 uppercase tracking-wider mb-4">Market Risk</h4>
                <p className="text-gray-400 leading-relaxed">
                  DeFi volume could collapse. Bridges could become unreliable. Regulatory pressure could kill cross-chain trading. The market we're building for might not exist in 2 years.
                </p>
              </div>
              
              <div className="border border-yellow-500/20 bg-yellow-500/5 p-8">
                <h4 className="text-lg font-mono text-yellow-400 uppercase tracking-wider mb-4">Execution Risk</h4>
                <p className="text-gray-400 leading-relaxed">
                  We might build it and nobody uses it. We might be wrong about what serious traders actually need. The improvements might be marginal instead of transformative.
                </p>
              </div>
              
              <div className="border border-yellow-500/20 bg-yellow-500/5 p-8">
                <h4 className="text-lg font-mono text-yellow-400 uppercase tracking-wider mb-4">Community Risk</h4>
                <p className="text-gray-400 leading-relaxed">
                  The 250 people might not actually contribute. Engagement might drop after month 3. The community build model might not work at this scale.
                </p>
              </div>
            </div>
            
            <div className="border-l-2 border-blue-500/30 pl-8 py-6 mt-12">
              <p className="text-xl text-white font-light mb-4">
                But here's why it's worth trying anyway:
              </p>
              <p className="text-lg text-gray-400 leading-relaxed mb-4">
                The current infrastructure is guaranteed not to work for large trades. The losses are real and recurring. The worst case scenario is we try and fail. The best case is we build the execution layer DeFi has needed since day one.
              </p>
              <p className="text-lg text-white font-light">
                For the 250 people who join us, that's a risk worth taking.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Join The Build */}
      <section className="py-20 px-6 border-t border-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-light mb-8 text-white tracking-tight">
            Join The Build
          </h2>
          
          <p className="text-2xl text-gray-400 font-light mb-12 leading-relaxed">
            250 spots. Real infrastructure. Actual alignment.
          </p>
          
          
          <a  href="/" className="inline-flex items-center gap-3 px-12 py-5 bg-white text-black font-mono text-sm uppercase tracking-wider hover:bg-gray-200 transition-all duration-300 group">
            Apply Now
            <ArrowLeft size={20} className="rotate-180 group-hover:translate-x-2 transition-transform" />
          </a>
        </div>
      </section>
    </div>
  );
}